import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Search } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal } from "@/components/Reveal";
import { FAQ_CATEGORIES } from "@/data/faq";

const CAT_ALL = "All";

export default function FAQ() {
  const [active, setActive] = useState(CAT_ALL);
  const [query, setQuery] = useState("");

  const categories = useMemo(() => [CAT_ALL, ...FAQ_CATEGORIES.map((c) => c.name)], []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FAQ_CATEGORIES
      .filter((c) => active === CAT_ALL || c.name === active)
      .map((c) => ({
        ...c,
        items: c.items.filter(
          (it) =>
            !q ||
            it.q.toLowerCase().includes(q) ||
            it.a.toLowerCase().includes(q)
        ),
      }))
      .filter((c) => c.items.length > 0);
  }, [active, query]);

  const totalItems = useMemo(
    () => filtered.reduce((acc, c) => acc + c.items.length, 0),
    [filtered]
  );

  // JSON-LD for SEO
  const jsonLd = useMemo(() => {
    const all = FAQ_CATEGORIES.flatMap((c) => c.items);
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: all.map((it) => ({
        "@type": "Question",
        name: it.q,
        acceptedAnswer: { "@type": "Answer", text: it.a },
      })),
    };
  }, []);

  return (
    <main className="bg-white" data-testid="faq-page">
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <section className="pt-32 md:pt-40 pb-12 bg-radial-soft relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-50 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-8 relative">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
              Frequently Asked Questions
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-medium leading-[1.02] tracking-tight text-[#06102A] max-w-4xl">
              Answers for institutions evaluating Vaultavo.
            </h1>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-7 max-w-2xl text-lg text-[#06102A]/70">
              Curated questions from banks, asset managers, custodians, and government
              partners — covering the Vaultavo Biometric Security Device, the Vaultavo Vault,
              compliance posture, custody models, and long-term vision.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-[#06102A]/10 bg-white sticky top-16 md:top-20 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-4 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex gap-2 overflow-x-auto">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                data-testid={`faq-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-xs uppercase tracking-[0.18em] font-semibold transition-colors ${
                  active === c
                    ? "bg-[#06102A] text-white"
                    : "bg-[#06102A]/[0.04] text-[#06102A]/70 hover:bg-[#06102A]/[0.08]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="md:ml-auto relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#06102A]/40" />
            <input
              data-testid="faq-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search questions"
              className="w-full rounded-full border border-[#06102A]/15 bg-white pl-9 pr-4 py-2 text-sm text-[#06102A] placeholder:text-[#06102A]/40 focus:outline-none focus:border-[#06102A] focus:ring-2 focus:ring-[#06102A]/10 transition-all"
            />
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24" data-testid="faq-content">
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          {totalItems === 0 ? (
            <p className="text-sm text-[#06102A]/60" data-testid="faq-empty">
              No questions match your search.
            </p>
          ) : (
            filtered.map((cat) => (
              <div key={cat.name} className="mb-14 last:mb-0">
                <Reveal>
                  <div className="flex items-end justify-between gap-4 mb-6">
                    <h2 className="font-display text-2xl md:text-3xl font-medium text-[#06102A]">
                      {cat.name}
                    </h2>
                    <span className="font-mono-vt text-xs text-[#06102A]/45">
                      {cat.items.length} {cat.items.length === 1 ? "question" : "questions"}
                    </span>
                  </div>
                </Reveal>

                <div className="glass-card rounded-2xl px-6 md:px-8 overflow-hidden">
                  <Accordion type="single" collapsible>
                    {cat.items.map((it, idx) => {
                      const slug = `${cat.name}-${idx}`
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-");
                      return (
                        <AccordionItem
                          key={slug}
                          value={slug}
                          className="border-b border-[#06102A]/[0.07] last:border-b-0"
                          data-testid={`faq-item-${slug}`}
                        >
                          <AccordionTrigger className="text-left text-[15px] md:text-base font-display font-medium text-[#06102A] hover:no-underline py-5 md:py-6">
                            {it.q}
                          </AccordionTrigger>
                          <AccordionContent className="pb-6 pr-6 text-[#06102A]/75 leading-relaxed text-[15px]">
                            {it.a.split(/\n\n+/).map((para, i) => (
                              <p key={i} className={i > 0 ? "mt-4" : ""}>
                                {para}
                              </p>
                            ))}
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <section className="py-20 bg-[#FAFBFC] border-t border-[#06102A]/10" data-testid="faq-cta">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h3 className="font-display text-3xl md:text-4xl font-medium text-[#06102A]">
            Still have questions?
          </h3>
          <p className="mt-4 text-[#06102A]/70">
            Speak with our institutional team about pilots, integrations, or technology
            demonstrations.
          </p>
          <Link
            to="/contact"
            data-testid="faq-cta-contact"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#06102A] text-white text-sm font-medium px-6 py-3 hover:-translate-y-0.5 transition-all"
          >
            Speak to the Team <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </main>
  );
}
